import { DocumentNode } from '@apollo/client';
import { SiteUrls } from '@tableau/shared-urls';
import { Formatters } from '../../../../../../../gen-src/Localize';
import { LabelFragment, InheritedLabelsFieldFragment, InheritedLabelsColumnFragment, InheritedLabelsFlowOutputFieldFragment } from '../../../../../../../gen-src/queries';
import { assetIconForType, groupBy } from '../../LabelHelpers';
import { HydratedCertificationRenderProps, RenderParentedCertification } from '../../certification/CertificationProps';
import { HydratedSensitivityRenderProps, RenderParentedSensitivity, RenderUpstreamSensitivity } from '../../sensitivity/SensitivityProps';
import { LabelContentItem, LabelRenderControls } from '../../shared/LabelRenderProps';
import { HydratedWarningRenderProps, RenderParentedWarning, RenderUpstreamWarning } from '../../warning/WarningProps';
import { ContextOverrides } from '../HookContext';
import { UseLabelsResult } from '../useRenderLabelsTypes';

export type GetContentLineageLabelsLazyQueryParams = {
  item: LabelContentItem,
  controls?: LabelRenderControls,
  includeUpstream?: boolean,
  includeParented?: boolean,
  document: DocumentNode,
  contextOverrides?: ContextOverrides,
}

type InheritedLabelsColumnAndFieldFragment = InheritedLabelsFieldFragment | InheritedLabelsColumnFragment;

type InheritedByCandidatesType = ReadonlyArray<InheritedLabelsColumnAndFieldFragment | InheritedLabelsFlowOutputFieldFragment>;

type convertLabelsFragmentIntoRenderPropsParams = {
  upstreamLabels?: LabelFragment[],
  item: LabelContentItem,
  controls: LabelRenderControls | undefined,
  parentedItems?: ReadonlyArray<{ labels: LabelFragment[] }>,
  includeUpstream?: boolean,
  includeParented?: boolean,
  inheritedByCandidates?: InheritedByCandidatesType,
}

type ContentUrlIdType = {
  readonly id: string;
  readonly vizportalUrlId?: string;
}

function urlForContentType(siteUrls: SiteUrls | undefined, contentType: string | undefined, id: string | undefined, parentId: string | undefined, parentType: string | undefined | null): string | undefined {
  if (!siteUrls || !contentType || !id) {
    return undefined;
  }
  switch (contentType) {
    case 'PublishedDatasource':
      return siteUrls.datasourceUrl(id);
    case 'Flow':
      return siteUrls.flowUrl(id);
    case 'CloudFile':
    case 'DatabaseServer':
    case 'File':
    case 'WebDataConnector':
      return siteUrls.catalogDatabaseUrl(id);
    case 'VirtualConnection':
      return siteUrls.virtualConnectionUrl(id);
    case 'VirtualConnectionTable':
      return siteUrls.virtualConnectionTableUrl(id);
    case 'DatabaseTable':
      return siteUrls.catalogTableUrl(id);
    case 'Column':
      if (parentType === 'VirtualConnectionTable') {
        return siteUrls.virtualConnectionTableLineageFieldUrl(parentId ?? '', id);
      }
      if (parentType === 'CustomSQLTable' || parentType === 'DatabaseTable') {
        return siteUrls.catalogTableColumnUrl(parentId ?? '', id);
      }
      return undefined;
    case 'Field':
      if (parentType === 'PublishedDatasource' || parentType === 'datasource') {
        return siteUrls.datasourceLineageFieldUrl(parentId ?? '', id);
      }
      if (parentType === 'Workbook' || parentType === 'workbook') {
        return siteUrls.workbookLineageFieldUrl(parentId ?? '', id);
      }
      if (parentType === 'Flow' || parentType === 'flow') {
        return siteUrls.flowLineageFieldUrl(parentId ?? '', id);
      }
      return undefined;
    default:
      return undefined;
  }
}

function getIdForContentURL(asset: ContentUrlIdType | null): string | undefined {
  if (!asset) {
    return undefined;
  }

  if ('vizportalUrlId' in asset) {
    return asset.vizportalUrlId;
  }

  return asset.id;
}

function buildLabelContentItemFromLabelFragment(label: LabelFragment, controls: LabelRenderControls | undefined): LabelContentItem {
  return {
    contentId: label.asset?.id,
    contentLuid: label.asset?.luid,
    contentName: label.asset?.name ?? undefined,
    contentType: label.asset?.__typename ?? null,
    contentIcon: assetIconForType(label.asset?.__typename ?? null, label.asset?.__typename === 'Column' ? label.asset.remoteType : undefined) ?? undefined,
    subjectArea: label.asset?.__typename,
    contentUrl: urlForContentType(controls?.siteUrls, label.asset?.__typename, getIdForContentURL(label.asset), label.asset?.__typename === 'Column' ? label.asset.table?.id : undefined, label.asset?.__typename === 'Column' ? label.asset.table?.__typename : undefined),
    remoteType: label.asset?.__typename === 'Column' ? label.asset.remoteType : undefined,
  }
}

function buildParentLabelContentItemFromLabelFragment(label: LabelFragment, controls: LabelRenderControls | undefined): LabelContentItem | undefined {
  if (label.asset?.__typename !== 'Column') {
    return undefined;
  }

  return {
    contentId: label.asset.table?.id,
    contentType: label.asset.table?.__typename === undefined || label.asset.table?.__typename === 'CustomSQLTable' ? null : label.asset.table?.__typename,
    contentName: label.asset.table?.name ?? undefined,
    contentUrl: urlForContentType(controls?.siteUrls, label.asset.table?.__typename, label.asset.table?.id, undefined, undefined),
  };
}

function buildInheritedByLabelContentItemFromLabelFragment(label: LabelFragment, controls: LabelRenderControls | undefined, item?: LabelContentItem, inheritedByCandidates?: InheritedByCandidatesType): LabelContentItem | undefined {
  if (!inheritedByCandidates || label.asset?.__typename !== 'Column') {
    return undefined;
  }

  const inheritedBy = inheritedByCandidates.filter(inheritedByCandidate => {
    const candidateAsFlow = (inheritedByCandidate as InheritedLabelsFlowOutputFieldFragment);
    const candidateAsColumnOrField = (inheritedByCandidate as InheritedLabelsColumnAndFieldFragment);
    if (candidateAsFlow.parentFields !== undefined) { // Flow
      return candidateAsFlow.parentFields.some(parentField => parentField.__typename === 'FlowColumnInputField' && parentField.column?.luid === label.asset?.luid);
    } else if (candidateAsColumnOrField.upstreamColumns !== undefined) { // Column and Field
      return candidateAsColumnOrField.upstreamColumns.some(column => column?.luid === label.asset?.luid);
    } else {
      return false;
    }
  }).shift();

  return inheritedBy ? {
    contentId: inheritedBy.id,
    contentType: null,
    contentName: inheritedBy.name ?? undefined,
    contentUrl: inheritedBy.__typename === 'Column' ?
      urlForContentType(controls?.siteUrls, 'Column', inheritedBy.id, getIdForContentURL(inheritedBy.table), inheritedBy.table?.__typename) :
      urlForContentType(controls?.siteUrls, 'Field', inheritedBy.id, item?.contentId, item?.subjectArea),
    subjectArea: inheritedBy.__typename === 'Column' ? 'Column' : 'Field'
  } : undefined;
}

function convertLabelFragmentToRenderLabelWarning(label: LabelFragment, controls: LabelRenderControls | undefined, item?: LabelContentItem, inheritedByCandidates?: InheritedByCandidatesType) {
  return {
    type: label.value,
    active: label.isActive,
    author: label.author ?? { name: '', username: '', domain: '' },
    message: label.message ?? '',
    updatedAt: Formatters.formatMediumDateTimeWithoutSeconds(new Date(label.updatedAt)),
    content: buildLabelContentItemFromLabelFragment(label, controls),
    parent: buildParentLabelContentItemFromLabelFragment(label, controls),
    severe: label.isElevated,
    inheritedBy: buildInheritedByLabelContentItemFromLabelFragment(label, controls, item, inheritedByCandidates),
  }
}

function convertLabelFragmentToRenderLabelCert(label: LabelFragment, controls: LabelRenderControls | undefined) {
  return {
    type: label.value,
    active: label.isActive,
    author: label.author ?? { name: '', username: '', domain: '' },
    message: label.message ?? '',
    updatedAt: Formatters.formatMediumDateTimeWithoutSeconds(new Date(label.updatedAt)),
    content: buildLabelContentItemFromLabelFragment(label, controls)
  };
}

function convertLabelFragmentToRenderLabelSensitivity(label: LabelFragment, controls: LabelRenderControls | undefined, downstreamItem?: LabelContentItem, inheritedByCandidates?: InheritedByCandidatesType) {
  return {
    type: label.value,
    active: label.isActive,
    author: label.author ?? { name: '', username: '', domain: '' },
    message: label.message ?? '',
    updatedAt: Formatters.formatMediumDateTimeWithoutSeconds(new Date(label.updatedAt)),
    content: buildLabelContentItemFromLabelFragment(label, controls),
    parent: buildParentLabelContentItemFromLabelFragment(label, controls),
    inheritedBy: buildInheritedByLabelContentItemFromLabelFragment(label, controls, downstreamItem, inheritedByCandidates)
  };
}

function getParentedItems(controls?: LabelRenderControls, includeParented?: boolean, parentedItems?: ReadonlyArray<{ labels: LabelFragment[] }>) {
  const parentedWarnings: RenderParentedWarning[] = [];
  const parentedCertifications: RenderParentedCertification[] = [];
  const parentedSensitivities: RenderParentedSensitivity[] = [];
  if (parentedItems && includeParented) {
    parentedItems.forEach((parentedItem) => {

      const currGrouped: Map<string, LabelFragment[]> = groupBy(parentedItem.labels, (key) => key.category);
      const currParentedWarnings: RenderParentedWarning[] | undefined = currGrouped.get('WARNING')?.map(label => {
        return convertLabelFragmentToRenderLabelWarning(label, controls);
      });

      const currParentedCertifications: RenderParentedCertification[] | undefined = currGrouped.get('CERTIFICATION')?.map(label => {
        return convertLabelFragmentToRenderLabelCert(label, controls);
      });

      const currParentedSensitivities: RenderParentedSensitivity[] | undefined = currGrouped.get('SENSITIVITY')?.map(label => {
        return convertLabelFragmentToRenderLabelSensitivity(label, controls);
      })

      if (currParentedWarnings) {
        parentedWarnings.push(...currParentedWarnings);
      }
      if (currParentedCertifications) {
        parentedCertifications.push(...currParentedCertifications);
      }
      if (currParentedSensitivities) {
        parentedSensitivities.push(...currParentedSensitivities);
      }
    });
  }

  return { parentedWarnings, parentedCertifications, parentedSensitivities };
}


export function convertLabelsFragmentIntoRenderProps({ upstreamLabels, item, controls, parentedItems, includeParented, includeUpstream, inheritedByCandidates }: convertLabelsFragmentIntoRenderPropsParams): Pick<UseLabelsResult, 'certifications' | 'warnings' | 'sensitivities'> {
  if (!upstreamLabels && !parentedItems) {
    return {};
  }

  const upstreamGrouped: Map<string, LabelFragment[]> | undefined = upstreamLabels && includeUpstream ? groupBy(upstreamLabels, (key) => key.category) : undefined;
  const upstreamWarnings: RenderUpstreamWarning[] | undefined = upstreamGrouped ? upstreamGrouped.get('WARNING')?.map(label => {
    return convertLabelFragmentToRenderLabelWarning(label, controls, item, inheritedByCandidates);
  }) : undefined;
  const upstreamSensitivities: RenderUpstreamSensitivity[] | undefined = upstreamGrouped ? upstreamGrouped.get('SENSITIVITY')?.map(label => {
    return convertLabelFragmentToRenderLabelSensitivity(label, controls, item, inheritedByCandidates);
  }) : undefined;

  const { parentedWarnings, parentedCertifications, parentedSensitivities } = getParentedItems(controls, includeParented, parentedItems);

  const hydratedWarnings: HydratedWarningRenderProps = {
    category: 'warning',
    content: item,
    kind: 'hydrated',
    upstream: upstreamWarnings ? { category: 'warning', labels: upstreamWarnings } : undefined,
    parented: parentedWarnings.length > 0 ? { category: 'warning', labels: parentedWarnings } : undefined
  };

  const hydratedCertifications: HydratedCertificationRenderProps = {
    category: 'certification',
    content: item,
    kind: 'hydrated',
    parented: parentedCertifications.length > 0 ? { category: 'certification', labels: parentedCertifications } : undefined,
  };

  const hydratedSensitivities: HydratedSensitivityRenderProps = {
    category: 'sensitivity',
    content: item,
    kind: 'hydrated',
    upstream: upstreamSensitivities ? { category: 'sensitivity', labels: upstreamSensitivities } : undefined,
    parented: parentedSensitivities.length > 0 ? { category: 'sensitivity', labels: parentedSensitivities } : undefined,
  }

  return {
    certifications: hydratedCertifications,
    warnings: hydratedWarnings,
    sensitivities: hydratedSensitivities,
  }
}
